import { preload } from 'react-dom';

import { RESPONSIVE_IMAGE_DEFAULTS } from '@/app/components/media/app-image/responsive-image/constants';
import {
  type ResponsiveImagePreloadHintsProps,
  type ResponsiveImageSource,
} from '@/app/components/media/app-image/responsive-image/types';
import {
  buildResponsiveImageSrcSet,
  buildResponsiveImageUrl,
  getDefaultWidthsForMedia,
} from '@/app/components/media/app-image/responsive-image/utils';
import { type ImageFetchPriority } from '@/app/components/media/app-image/shared/image-load-state';
import { isSvgSrc } from '@/app/components/media/app-image/shared/image-source';
import { toStableImageKitParamsKey } from '@/app/components/media/app-image/shared/imagekit-transform';
import { getProjectImageKitDefaultTransforms } from '@/app/components/media/app-image/shared/project-adapter';
import {
  getImageResourceHintOrigin,
  preconnectResourceHintOrigin,
} from '@/app/components/media/app-image/shared/resource-hints';

type ResponsiveImagePreloadOptions = NonNullable<Parameters<typeof preload>[1]>;

export const getResponsiveImagePreloadOptions = (
  source: ResponsiveImageSource,
  fetchPriority?: ImageFetchPriority
): ResponsiveImagePreloadOptions => {
  const isSvg = isSvgSrc(source.src);
  const fetchPriorityOption = fetchPriority ? { fetchPriority } : {};

  if (isSvg) {
    const media = source.media ?? undefined;
    return {
      as: 'image',
      ...fetchPriorityOption,
      ...(media ? { media } : {}),
    };
  }

  const widths = source.widths ?? getDefaultWidthsForMedia(source.media);
  const sizes = source.sizes ?? RESPONSIVE_IMAGE_DEFAULTS.sizes.desktop;
  const media = source.media ?? '';
  const params = source.params;

  const ws = Array.from(widths);
  return {
    as: 'image',
    ...fetchPriorityOption,
    media,
    imageSrcSet: buildResponsiveImageSrcSet(source.src, ws, params, {
      defaultTransforms: getProjectImageKitDefaultTransforms(),
    }),
    imageSizes: sizes,
  };
};

export const preloadResponsiveImage = (
  source: ResponsiveImageSource,
  fetchPriority?: ImageFetchPriority
): void => {
  const href = buildResponsiveImageUrl(source, {
    defaultTransforms: getProjectImageKitDefaultTransforms(),
  });

  preload(href, {
    ...getResponsiveImagePreloadOptions(source, fetchPriority),
  });
};

export const getResponsiveImagePreloadKey = (
  source: ResponsiveImageSource
): string => {
  if (isSvgSrc(source.src)) return `svg|${source.src}|${source.media ?? ''}`;

  const widths = source.widths ?? getDefaultWidthsForMedia(source.media);
  const sizes = source.sizes ?? RESPONSIVE_IMAGE_DEFAULTS.sizes.desktop;
  const params = toStableImageKitParamsKey(source.params);

  return [
    'img',
    source.src,
    source.media ?? '',
    sizes,
    Array.from(widths).join(','),
    params,
  ].join('|');
};

export const ResponsiveImagePreloadHints = ({
  fetchPriority,
  sources,
}: ResponsiveImagePreloadHintsProps) => {
  if (sources.length === 0) return null;

  const seen = new Set<string>();
  const seenOrigins = new Set<string>();
  for (const s of sources) {
    const origin = getImageResourceHintOrigin(s.src);
    if (origin && !seenOrigins.has(origin)) {
      seenOrigins.add(origin);
      preconnectResourceHintOrigin(origin);
    }

    const key = getResponsiveImagePreloadKey(s);
    if (seen.has(key)) continue;

    seen.add(key);
    preloadResponsiveImage(s, fetchPriority);
  }

  return null;
};
